import { useMutation, useQuery } from "@apollo/client/react";
import { GQLQuery, GQLMutation } from "@rx-cs-system/schema";
import { useEffect } from "react";
import { useNavigate, useParams, useLocation, Location } from "react-router";

import { useHandleError } from "../../../hooks";
import { GET_HT_USER } from "../../../graphql/queries";
import { parsePatientHt } from "../../../graphql/utils";
import {
  PatientHtDetailPage as Component,
  PatientHtProps,
} from "../../../components/pages/patientHt/PatientHtDetailPage";
import { HtPatient } from "../../../components/pages/patientHt/UpdateEmailAddressPage";
import { FORCE_CANCEL_PRESCRIPTION } from "../../../graphql/mutations";
import { useSnackBar } from "../../../hooks/useSnackBar";
import { useCheckUserRole } from "../../../hooks/useCheckUserRole";

export const PatientHtDetailPage = ({}) => {
  const navigate = useNavigate();

  useCheckUserRole();
  const { snackBar, showOKSnackBar } = useSnackBar();
  const location: Location<{
    emailOnChanged?: {
      id: string;
      email: string;
      message: string;
    };
  }> = useLocation();
  const emailOnChanged = location.state?.emailOnChanged;

  useEffect(() => {
    const shouldShowSnackBar = emailOnChanged && !snackBar?.isOpen;
    if (!shouldShowSnackBar) {
      return;
    }
    showOKSnackBar(emailOnChanged.message);
    navigate(location.pathname + location.search, {
      replace: true,
      state: null,
    });
  }, [emailOnChanged, showOKSnackBar, snackBar, location, navigate]);

  const { id: patientId } = useParams<{ id: string }>();
  if (!patientId) throw new Error("patientId is undefined");

  const { error, data } = useQuery<GQLQuery>(GET_HT_USER, {
    variables: { id: patientId },
  });
  const [forceCancelPrescription] = useMutation<GQLMutation>(
    FORCE_CANCEL_PRESCRIPTION,
    {
      onCompleted: (result) => {
        if (
          result.forceCancelPrescription?.__typename ===
          "ForceCancelPrescriptionError"
        ) {
          navigate(`/cancelPrescription/error`, {
            state: {
              message: `ForceCancelPrescriptionError: ${result.forceCancelPrescription.message}`,
              prescriptionCode: result.forceCancelPrescription.prescriptionCode,
            },
          });
        } else {
          showOKSnackBar("処方をキャンセルしました。");
        }
      },
      onError: (e) => {
        navigate(`/cancelPrescription/error`, {
          state: {
            message: `onError: ${e.message}`,
            prescriptionCode: data?.htUser.prescriptionCode,
          },
        });
      },
    },
  );
  useHandleError(error);

  if (!data?.htUser) {
    return null;
  }
  const htUser = data.htUser;

  const handleUpdateEmailAddressButtonClick = async () => {
    navigate(`/patient_ht/${patientId}/updateEmailAddress`, {
      state: {
        id: patientId,
        email: htUser.email,
      } as HtPatient,
    });
  };

  const handlePublishVerifyCodeButtonClick = () => {
    navigate(`/patient_ht/${patientId}/publish-verify-code`, {
      state: {
        id: patientId,
        email: htUser.email,
      } as HtPatient,
    });
  };

  const handleCancelPrescriptionCodeButtonClick = async () => {
    const isOk = confirm(
      "本当に処方をキャンセルしますか？キャンセルしたら元には戻せません。",
    );
    if (isOk) {
      await forceCancelPrescription({
        variables: {
          input: {
            prescriptionCode: data?.htUser.prescriptionCode,
          },
        },
      });
    }
  };

  const props: PatientHtProps = {
    patientHt: parsePatientHt(htUser),
    onUpdateEmailAddressButtonClick: handleUpdateEmailAddressButtonClick,
    onPublishVerifyCodeButtonClick: handlePublishVerifyCodeButtonClick,
    onCancelPrescriptionCodeButtonClick:
      handleCancelPrescriptionCodeButtonClick,
    maskedDetail: data?.htUser.maskedDetail,
    snackBar,
  };

  return <Component {...props} />;
};
